import React, { useState, useEffect } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  Button,
  Textarea,
  Divider,
  Input,
  Accordion,
  AccordionItem,
  Code,
} from "@nextui-org/react";
import { v4 as uuidv4 } from "uuid";
import formComponentRegistry, { FormComponentType } from "./formComponents";

interface FormElement {
  id: string;
  type: FormComponentType;
  config: {
    [key: string]: any;
  };
  validation?: {
    pattern?: string;
    message?: string;
    custom?: string;
  };
  path?: string;
  parentPath?: string;
  isArray?: boolean;
  isObject?: boolean;
  label?: string;
  children?: FormElement[];
}

// Helper to check if a string is a valid JS identifier
const isValidIdentifier = (key: string) => /^[a-zA-Z_$][a-zA-Z0-9_$]*$/.test(key);

// Helper function to build a path segment (dot for valid, bracket for special)
const buildPath = (parentPath: string, key: string) => {
  if (parentPath) {
    if (isValidIdentifier(key)) {
      return parentPath + '.' + key;
    } else {
      return parentPath + '["' + key.replace(/"/g, '\\"') + '"]';
    }
  } else {
    if (isValidIdentifier(key)) {
      return key;
    } else {
      return '["' + key.replace(/"/g, '\\"') + '"]';
    }
  }
};

// Robust getNestedValue: supports dot, bracket, and array index notation
const getNestedValue = (obj: any, path: string) => {
  if (!path) return obj;
  // Tokenize: dot notation, bracket notation, and array indices
  const regex = /(?:\.([a-zA-Z_$][a-zA-Z0-9_$]*)|\["((?:[^"\\]|\\.)*)"\]|\[(\d+)\])/g;
  let match;
  let curr = obj;
  let lastIndex = 0;
  // If path starts with a valid identifier, match it first
  if (/^[a-zA-Z_$][a-zA-Z0-9_$]*/.test(path)) {
    const firstKey = path.match(/^[a-zA-Z_$][a-zA-Z0-9_$]*/)?.[0];
    curr = curr?.[firstKey!];
    lastIndex = firstKey!.length;
  }
  regex.lastIndex = lastIndex;
  while ((match = regex.exec(path))) {
    if (match.index > lastIndex) {
      // There is an unmatched segment, treat as error
      return undefined;
    }
    lastIndex = regex.lastIndex;
    if (match[1]) {
      curr = curr?.[match[1]];
    } else if (match[2]) {
      curr = curr?.[match[2]];
    } else if (match[3]) {
      curr = Array.isArray(curr) ? curr[parseInt(match[3], 10)] : undefined;
    }
    if (curr === undefined) return undefined;
  }
  return curr;
};

// Helper function to set value in nested path
const setNestedValue = (obj: any, path: string, value: any) => {
  const parts = path.split('.');
  const lastPart = parts.pop()!;
  const target = parts.reduce((acc, part) => {
    if (!acc[part]) acc[part] = {};
    return acc[part];
  }, obj);
  target[lastPart] = value;
  return obj;
};

// Helper function to create form elements from nested data
const createFormElements = (
  data: any,
  parentPath: string = '',
  elements: FormElement[] = [],
  fieldMappings: Record<string, { label: string, type: FormComponentType }> = {}
): FormElement[] => {
  if (Array.isArray(data)) {
    // Create a container element for the array
    const arrayId = uuidv4();
    const arrayLabel = parentPath.split('.').pop()?.split('_').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ') || 'Array';

    const arrayElement: FormElement = {
      id: arrayId,
      type: "TextInput", // This will be replaced with a custom array renderer
      path: parentPath,
      parentPath,
      isArray: true,
      label: arrayLabel,
      config: {
        label: arrayLabel,
      },
      children: []
    };

    // Process each array item
    data.forEach((item, index) => {
      const arrayPath = `${parentPath}[${index}]`;
      if (typeof item === 'object' && item !== null) {
        // Create a container for the object
        const objectId = uuidv4();
        const objectElement: FormElement = {
          id: objectId,
          type: "TextInput", // This will be replaced with a custom object renderer
          path: arrayPath,
          parentPath: arrayId,
          isObject: true,
          config: {
            label: `Item ${index + 1}`,
          },
          children: []
        };
        arrayElement.children?.push(objectElement);
        createFormElements(item, arrayPath, objectElement.children || [], fieldMappings);
      } else {
        // Handle primitive array items
        const id = uuidv4();
        arrayElement.children?.push({
          id,
          type: typeof item === 'string' && item.length > 100 ? "RichTextInput" : "TextInput",
          path: arrayPath,
          parentPath: arrayId,
          config: {
            label: `Item ${index + 1}`,
            placeholder: `Enter value for item ${index + 1}`,
            required: true,
          },
        });
      }
    });

    elements.push(arrayElement);
  } else if (typeof data === 'object' && data !== null) {
    // Create a container for the object
    const objectId = uuidv4();
    let objectLabel = parentPath.split('.').pop()?.split('_').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ') || '';
    // For nested labels like Explanation["Repair Profile Score"], show only the last segment
    if (objectLabel.includes('["')) {
      const match = objectLabel.match(/\["(.+?)"\]/);
      if (match && match[1]) {
        objectLabel = match[1].split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
      }
    } else if (objectLabel.includes('.')) {
      const last = objectLabel.split('.').pop();
      if (last) objectLabel = last.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    }
    // If the label is just a number (e.g., '0'), set to ''
    if (/^\d+$/.test(objectLabel)) {
      objectLabel = '';
    }
    
    

    const objectElement: FormElement = {
      id: objectId,
      type: "TextInput", // This will be replaced with a custom object renderer
      path: parentPath,
      parentPath,
      isObject: true,
      label: objectLabel,
      config: {
        label: objectLabel,
      },
      children: []
    };

    // Process object properties
    Object.entries(data).forEach(([key, value]) => {
      const currentPath = buildPath(parentPath, key);

      if (typeof value === 'object' && value !== null) {
        createFormElements(value, currentPath, objectElement.children || [], fieldMappings);
      } else {
        const id = uuidv4();
        const mapping = fieldMappings[key] || {
          label: key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '),
          type: typeof value === 'string' && value.length > 100 ? "RichTextInput" : "TextInput"
        };

        objectElement.children?.push({
          id,
          type: mapping.type,
          path: currentPath,
          parentPath: objectId,
          config: {
            ...formComponentRegistry[mapping.type].config,
            label: mapping.label,
            placeholder: `Enter ${mapping.label.toLowerCase()}`,
            required: true,
          },
        });
      }
    });

    elements.push(objectElement);
  }
  return elements;
};

// Helper function to convert form values back to nested structure
const formValuesToNested = (formElements: FormElement[], formValues: Record<string, any>): Record<string, any> => {
  const result: Record<string, any> = {};

  const processElement = (element: FormElement) => {
    if (element.path) {
      setNestedValue(result, element.path, formValues[element.id]);
    }
    element.children?.forEach(processElement);
  };

  formElements.forEach(processElement);
  return result;
};

export interface JsonFormBuilderProps {
  onSubmit?: (values: Record<string, any>) => void | Promise<void>;
  submitButtonText?: string;
  showSubmitButton?: boolean;
  initialJson?: string;
  jsonData?: Record<string, any>;
  readOnly?: boolean;
  onChange?: (values: Record<string, any>) => void;
  isDisabled?: boolean;
}

const JsonFormBuilder: React.FC<JsonFormBuilderProps> = ({
  onSubmit,
  submitButtonText = "Submit",
  showSubmitButton = true,
  initialJson,
  jsonData,
  readOnly = false,
  onChange,
  isDisabled = false,
}) => {
  const [jsonText, setJsonText] = useState<string>(initialJson || '{}');
  const [jsonError, setJsonError] = useState<string>("");
  const [formElements, setFormElements] = useState<FormElement[]>([]);
  const [formValues, setFormValues] = useState<Record<string, any>>({});
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [formTitle, setFormTitle] = useState<string>("JSON Form");

  // Parse JSON and convert to form elements
  const parseJsonToForm = (data: Record<string, any>) => {
    try {
      setJsonError("");

      // Create form elements from the data
      const elements = createFormElements(data);
      setFormElements(elements);

      // Set initial form values
      const initialValues: Record<string, any> = {};
      // Helper function to process nested elements and set their values
      const processElement = (element: FormElement) => {
        if (element.path) {
          initialValues[element.id] = getNestedValue(data, element.path);
        }
        element.children?.forEach(processElement);
      };
      elements.forEach(processElement);
      setFormValues(initialValues);

      if (elements.length === 0) {
        throw new Error('No valid fields found in the JSON');
      }
    } catch (error) {
      console.error("JSON parsing error:", error);
      setJsonError(error instanceof Error ? error.message : "Invalid JSON format");
    }
  };

  // Handle form input change
  const handleChange = (id: string, value: any) => {
    setFormValues((vals) => ({ ...vals, [id]: value }));

    // Clear error when user starts typing
    if (formErrors[id]) {
      setFormErrors((errors) => {
        const newErrors = { ...errors };
        delete newErrors[id];
        return newErrors;
      });
    }

    // Call onChange if provided
    if (onChange) {
      const updatedValues = { ...formValues, [id]: value };
      const nestedValues = formValuesToNested(formElements, updatedValues);
      onChange(nestedValues);
    }
  };

  // Render form elements with proper grouping
  const renderFormElements = (elements: FormElement[]) => {
    return elements.map(element => {
      if (element.isArray) {
        return (
          <Accordion key={element.id} className="mb-4">
            <AccordionItem
              key={element.id}
              title={element.label}
              subtitle={`${element.children?.length || 0} items`}
            >
              <div className="pl-4 space-y-4">
                {element.children?.map(child => renderFormElements([child]))}
              </div>
            </AccordionItem>
          </Accordion>
        );
      }

      if (element.isObject) {
        return (
          <div key={element.id} className="mb-4">
            <h3 className="text-lg font-semibold mb-2">{element.label}</h3>
            <div className="pl-4 space-y-4">
              {element.children?.map(child => renderFormElements([child]))}
            </div>
          </div>
        );
      }

      const Component = formComponentRegistry[element.type].component;
      // Determine the value to display
      let displayValue = formValues[element.id];
      if (displayValue === 0) {
        displayValue = '0';
      } else if (typeof displayValue === 'number') {
        // Show as 1.0 if originally 1.0, otherwise as is
        if (Number.isInteger(displayValue)) {
          displayValue = displayValue.toString();
        } else {
          displayValue = displayValue.toFixed(2).replace(/\.00$/, '.0');
        }
      } else if (displayValue === undefined || displayValue === null) {
        displayValue = '';
      }
      return (
        <div key={element.id} id={element.id} className="mb-4">
          <Component
            {...element.config}
            value={displayValue}
            onChange={readOnly ? undefined : (e: any) =>
              handleChange(element.id, e?.target?.value !== undefined ? e.target.value : e)
            }
            isInvalid={!readOnly && !!formErrors[element.id]}
            errorMessage={!readOnly ? formErrors[element.id] : undefined}
            label={element.config.label || formComponentRegistry[element.type].label}
            readOnly={readOnly}
            isReadOnlyMode={readOnly}
          />
        </div>
      );
    });
  };

  // Effect to parse JSON data when provided
  useEffect(() => {
    if (jsonData) {
      parseJsonToForm(jsonData);
    }
  }, [jsonData]);

  // Effect to parse initial JSON on component mount
  useEffect(() => {
    if (!jsonData && initialJson) {
      try {
        const data = JSON.parse(initialJson);
        parseJsonToForm(data);
      } catch (error) {
        setJsonError(`JSON syntax error: ${error instanceof Error ? error.message : "Invalid JSON"}`);
      }
    }
  }, [initialJson]);

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    let errors: Record<string, string> = {};

    // Validate all form elements
    const validateElement = (element: FormElement) => {
      if (!element.isArray && !element.isObject) {
        const error = formComponentRegistry[element.type].validate(
          formValues[element.id],
          element.config
        );
        if (error) errors[element.id] = error;
      }
      element.children?.forEach(validateElement);
    };
    formElements.forEach(validateElement);

    setFormErrors(errors);

    // If no errors, submit the form
    if (Object.keys(errors).length === 0) {
      try {
        setIsSubmitting(true);
        const nestedValues = formValuesToNested(formElements, formValues);
        await onSubmit?.(nestedValues);
      } catch (error) {
        console.error("Form submission error:", error);
        alert("An error occurred during form submission");
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  // If jsonData is provided, only show the form
  if (jsonData) {
    return (
      <div className="w-full h-full">
        <Card className="w-full h-full">
          <CardBody className="overflow-auto">
            <form onSubmit={handleSubmit} className="space-y-4">
              {formElements.length === 0 ? (
                <div className="flex items-center justify-center h-[300px]">
                  <p className="text-default-500">
                    No form elements to display.
                  </p>
                </div>
              ) : (
                <>
                  {renderFormElements(formElements)}
                  {showSubmitButton && !readOnly && (
                    <Button
                      type="submit"
                      color="primary"
                      className="mt-4"
                      isLoading={isSubmitting}
                      disabled={isDisabled || isSubmitting}
                    >
                      {submitButtonText}
                    </Button>
                  )}
                </>
              )}
            </form>
          </CardBody>
        </Card>
      </div>
    );
  }

  // If no jsonData, show the full editor with JSON input
  return (
    <div style={{
      display: "flex",
      height: "calc(100vh - 40px)",
      gap: "16px",
      padding: "16px",
      width: "100%",
    }}>
      {/* JSON Editor - Left Side */}
      <div style={{ width: "50%", display: "flex", flexDirection: "column" }}>
        <Card className="w-full h-full">
          <CardHeader>
            <div className="flex w-full justify-between items-center">
              <h3 className="text-lg font-semibold">JSON Editor</h3>
              <div className="flex gap-2">
                <Button
                  color="primary"
                  onPress={() => {
                    try {
                      const data = JSON.parse(jsonText);
                      parseJsonToForm(data);
                    } catch (error) {
                      setJsonError(`JSON syntax error: ${error instanceof Error ? error.message : "Invalid JSON"}`);
                    }
                  }}
                >
                  Generate Form
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardBody>
            <Textarea
              value={jsonText}
              onChange={(e) => setJsonText(e.target.value)}
              minRows={20}
              placeholder="Enter your JSON here..."
              className="font-mono text-sm h-full"
              isInvalid={!!jsonError}
              errorMessage={jsonError}
              description="Enter JSON data. Click 'Generate Form' to update the preview."
              variant="bordered"
              color={jsonError ? "danger" : "default"}
            />
            {jsonError && (
              <div className="mt-2 p-2 bg-danger-50 text-danger rounded-md text-sm">
                <strong>Error:</strong> {jsonError}
              </div>
            )}
          </CardBody>
        </Card>
      </div>

      {/* Form Preview - Right Side */}
      <div style={{ width: "50%", display: "flex", flexDirection: "column" }}>
        <Card className="w-full h-full">
          <CardHeader>
            <div className="flex w-full justify-between items-center">
              <Input
                type="text"
                label="Form Title"
                value={formTitle}
                onChange={(e) => setFormTitle(e.target.value)}
                className="max-w-sm"
              />
            </div>
          </CardHeader>
          <Divider />
          <CardBody className="overflow-auto">
            <form onSubmit={handleSubmit} className="space-y-4">
              {formElements.length === 0 ? (
                <div className="flex items-center justify-center h-[300px]">
                  <p className="text-default-500">
                    Edit the JSON on the left and click "Generate Form" to preview.
                  </p>
                </div>
              ) : (
                <>
                  {renderFormElements(formElements)}
                  {showSubmitButton && (
                    <Button
                      type="submit"
                      color="primary"
                      className="mt-4"
                      isLoading={isSubmitting}
                      disabled={isDisabled || isSubmitting}
                    >
                      {submitButtonText}
                    </Button>
                  )}
                </>
              )}
            </form>
          </CardBody>
        </Card>
      </div>
    </div>
  );
};

export default JsonFormBuilder; 